import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Alert, Avatar, Paper, Snackbar, Tooltip } from "@mui/material"
import { motion } from "framer-motion"
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService"
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents"
import BusinessIcon from "@mui/icons-material/Business"
import ListAltIcon from "@mui/icons-material/ListAlt"
import PermIdentityIcon from "@mui/icons-material/PermIdentity"
import LogoutIcon from "@mui/icons-material/Logout"
import { useApiService } from "../hooks/Api/useApiService"
import Sphere from "../UI/Sphere"
import Icn from "../UI/SharieIcn/SharieIcn"
import useTranslationSharie from "../hooks/Language/useTranslation"
import SurveyModal from "../Modals/SurveyModal/SurveyModal"
import { useBusiness } from "../hooks/Contexts/BusinessContext/BusinessContext"
import logo from "../../assets/images/shariehandslogo.png"
import SharieCalendar from "../UI/SharieCalendar"
import useDeviceType from "../hooks/Utils/useDeviceType"
import island from "../../assets/images/tropical_island.mp4"
import confetti from "canvas-confetti"
import treasure from "../../assets/images/chest.png"
import WisdomModal from "../Modals/WisdomModal/WisdomModal"
import LinksActivity from "../LinksActivity/LinksActivity"
import { useSocket } from "../hooks/Utils/Socket/SocketManager"
import RightSidebar from "./RightSidebar"

const STORAGE_KEY = "myIslandPositions"

export default function MyIsland({ setIsAuthenticated }) {
  const navigate = useNavigate()
  const { getData, postData } = useApiService()
  const [user, setUser] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [image, setImage] = useState(island)
  const { context_business } = useBusiness()
  const surveyRef = useRef()
  const aiRef = useRef()
  const t = useTranslationSharie()
  const deviceType = useDeviceType()
  const isDesktop = deviceType === "desktop"
  const defaultPositions = {
    avatar: { x: 20, y: 20 },
    skills: { x: 20, y: 250 },
    activity: { x: 20, y: 350 },
    business: { x: 20, y: 450 },
    profile: { x: 20, y: 150 },
    orders: { x: 20, y: 550 },
    logout: { x: 20, y: 650 },
    sphere: { x: 150, y: 50 },
    calendar: { x: 1150, y: 50 },
    links: { x: 750, y: 70 },
  }
  const [hiddenItem, setHiddenItem] = useState(null)
  const [treasureFound, setTreasureFound] = useState(false)
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [positions, setPositions] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        return defaultPositions
      }
    }
    return defaultPositions
  })
  const [anchorEl, setAnchorEl] = useState(null)
  const socket = useSocket()
  const videoRef = useRef()
  const [zoom, setZoom] = useState(1)
  const [origin, setOrigin] = useState("50% 50%")

  const items = [
    {
      icon: null,
      label: "avatar",
      to: "",
    },
    {
      icon: <HomeRepairServiceIcon fontSize='large' sx={{ color: "#9c27b0" }} />,
      label: "skills",
      to: "/skills",
    },
    {
      icon: <EmojiEventsIcon fontSize='large' sx={{ color: "gold" }} />,
      label: "activity",
      to: "/activity",
    },
    {
      icon: <BusinessIcon fontSize='large' sx={{ color: "brown" }} />,
      label: "business",
      to: "/business",
    },
    {
      icon: <ListAltIcon fontSize='large' sx={{ color: "forestgreen" }} />,
      label: "orders",
      to: "/orders",
    },
    {
      icon: <PermIdentityIcon fontSize='large' sx={{ color: "#1976d2" }} />,
      label: "profile",
      to: "/profile",
    },
    { icon: <LogoutIcon fontSize='large' sx={{ color: "red" }} />, label: "logout", to: "/" },

    {
      icon: <Sphere image={logo} />,
      label: "sphere",
      to: "",
    },
    {
      icon: <SharieCalendar />,
      label: "calendar",
      to: "",
    },
    {
      icon: <LinksActivity />,
      label: "links",
      to: "",
    },
  ]

  useEffect(() => {
    postData("users/getuser", { user_name: localStorage.getItem("user") }).then((data) => {
      const user = data?.user[0]
      setUser(user)
      if (!treasureFound && user?.hidden_item) {
        setHiddenItem({ x: user.hidden_item.x, y: user.hidden_item.y })
      } else {
        setHiddenItem(null)
      }
    })
  }, [showSnackbar])

  useEffect(() => {
    socket?.on("receive_follower_notification", (data) => {
      setNotifications((prev) => [...prev, data.notification])
    })

    return () => {
      socket?.off("receive_follower_notification")
    }
  }, [])

  const handleAIClick = () => {
    if (aiRef.current) {
      aiRef.current.childFunction()
    }
  }

  const openSurveyModal = useCallback(() => {
    if (surveyRef.current) {
      surveyRef.current.childFunction()
    }
  }, [])

  const handleBackgroundDoubleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()

    const clickX = e.clientX - rect.left
    const clickY = e.clientY - rect.top
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100

    if (zoom === 1) {
      setZoom(2) // zoom in x2
      setOrigin(`${xPercent}% ${yPercent}%`)
    } else {
      setZoom(1) // zoom out
      setOrigin("50% 50%")
    }
    if (hiddenItem) {
      const hiddenX = (hiddenItem.x / 100) * rect.width
      const hiddenY = (hiddenItem.y / 100) * rect.height
      const distance = Math.sqrt((clickX - hiddenX) ** 2 + (clickY - hiddenY) ** 2)
      if (distance <= 50 && !treasureFound) {
        triggerTreasureFound()
      }
    }
  }

  const handleRestoreIcons = () => {
    localStorage.removeItem(STORAGE_KEY)
    setPositions(defaultPositions)
  }

  const triggerTreasureFound = async () => {
    setTreasureFound(true)
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    })
    setShowSnackbar(true)
    //added to try to remove chest
  }

  const handleTreasureConfirm = async () => {
    setShowSnackbar(false)
    setTreasureFound(false)
    setHiddenItem(null)
    await postData("sharie/foundhiddenitem", { user_name: localStorage.getItem("user") })
  }

  const handleDragEnd = (label, info) => {
    setPositions((prev) => {
      const prevPos = prev[label] || { x: 0, y: 0 }
      const updated = {
        ...prev,
        [label]: {
          x: prevPos.x + info.offset.x,
          y: prevPos.y + info.offset.y,
        },
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
      return updated
    })
  }

  const doSignOut = async () => {
    await getData("users/logout")
      .then(() => {})
      .catch(() => {})
    setIsAuthenticated(false)
    navigate("/")
  }

  const handleDoubleClick = (to) => {
    if (to === "") return

    if (to === "/") {
      handleClickLogout()
    } else if (to === "/profile") {
      navigate(to, { user_name: localStorage.getItem("user"), editable: true })
    } else if (context_business && to === "/business") {
      navigate("/businesspage")
    } else if (to === "/business") {
      navigate("/business")
    } else navigate(to)
  }

  const handleClickLogout = () => {
    postData("sharie/getsurveyseen", { user_name: localStorage.getItem("user") }).then((data) => {
      if (data?.survey === "seen") {
        doSignOut()
      } else {
        openSurveyModal()
      }
    })
  }

  const getDisplayItem = (label, icon) => {
    if (label === "sphere")
      return <Icn tooltip='Get Tips' onClick={handleAIClick} icon={<Sphere image={logo} />} />
    else if (label === "calendar" && isDesktop) return <SharieCalendar />
    else if (label === "links" && isDesktop) return <LinksActivity notifications={notifications} />
    else if (label === "avatar")
      return (
        <Tooltip title={t("Restore Icons")}>
          <Avatar
            src={user?.image}
            sx={{
              width: 80,
              height: 80,
              position: "absolute",
              border: "3px solid #1976d2",
              cursor: "pointer",
            }}
            onClick={handleRestoreIcons}
          />
        </Tooltip>
      )
    // else if (label === "linknetwork" && isDesktop)
    //   return <LinkNetwork user_name={localStorage.getItem("user")} image={context_user?.image} />
    else
      return (
        <Tooltip title={t(label)}>
          <Paper
            elevation={6}
            sx={{
              width: 80,
              height: 80,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.8)",
              backdropFilter: "blur(6px)",
            }}
          >
            {icon}
          </Paper>
        </Tooltip>
      )
  }

  return (
    <div
      style={{
        height: "94vh",
        width: "100vw",
        // backgroundImage: `url(${image})`,
        // backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        overflow: "hidden",
        cursor: "zoom-in",
      }}
      onDoubleClick={handleBackgroundDoubleClick}
    >
      <video
        ref={videoRef}
        src={image}
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -1,
          transform: `scale(${zoom})`,
          transformOrigin: origin,
          transition: "transform 0.3s ease",
        }}
      />

      {treasureFound && (
        <div
          onClick={handleTreasureConfirm}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 20,
            cursor: "pointer",
            textAlign: "center",
          }}
        >
          <img src={treasure} alt='Treasure Found' style={{ maxWidth: 300 }} />
          <div
            style={{
              background: "rgba(0,0,0,0.7)",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: 12,
              marginTop: 12,
              fontSize: "1.2rem",
            }}
          >
            🎉 You found it! Good job! 🎉
            <br />
            Click to confirm
          </div>
        </div>
      )}
      <SurveyModal
        ref={surveyRef}
        setAnchorEl={anchorEl}
        setIsAuthenticated={setIsAuthenticated}
        signout={true}
      />
      <WisdomModal ref={aiRef} />

      {items.map(({ label, icon, to }) => (
        <motion.div
          key={label}
          drag
          dragMomentum={false}
          dragElastic={0.2}
          initial={positions[label]}
          animate={positions[label]}
          layout
          onDragEnd={(e, info) => handleDragEnd(label, info)}
          whileHover={{ scale: label !== "calendar" ? 1.2 : 1 }}
          whileTap={{ scale: label !== "calendar" ? 0.9 : 1 }}
          style={{ position: "absolute", cursor: "grab" }}
          onDoubleClick={label !== "calendar" ? () => handleDoubleClick(to) : undefined}
        >
          {getDisplayItem(label, icon)}
        </motion.div>
      ))}

      {hiddenItem && !treasureFound && (
        <img
          src={treasure} // optional: use a semi-transparent treasure chest or X icon
          alt='hidden item'
          style={{
            position: "absolute",
            top: `${hiddenItem.y}%`,
            left: `${hiddenItem.x}%`,
            width: 25,
            height: 25,
            opacity: 0.5,
            pointerEvents: "none", // so it does not block double-click
            zIndex: 5,
          }}
        />
      )}
      <RightSidebar />
      <Snackbar open={showSnackbar} autoHideDuration={4000} onClose={() => setShowSnackbar(false)}>
        <Alert onClose={() => setShowSnackbar(false)} severity='success' sx={{ width: "100%" }}>
          You found the hidden item! You got 100 credit 🎉
        </Alert>
      </Snackbar>
    </div>
  )
}
